import React, { useState, useEffect } from 'react';
import { DesktopAd } from './DesktopAd';
import { MobileStickyAd } from './MobileStickyAd';
import { shouldShowAds, isTrackingPreventionEnabled, getAdErrorMessage, suppressTrackingWarnings, checkAdSenseScriptLoad, isFirefoxETPEnabled } from './AdUtils';
import FallbackAd from './FallbackAd';
import FirefoxAdFallback from './FirefoxAdFallback';
import './ads.css';

interface AdManagerProps {
  className?: string;
}

export const AdManager: React.FC<AdManagerProps> = ({ className = '' }) => {
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [adsLoaded, setAdsLoaded] = useState(false);
  const [initialScreenType, setInitialScreenType] = useState<'mobile' | 'desktop' | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  const [isAdBlockerDetected, setIsAdBlockerDetected] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  // Track window resize with debouncing to prevent rapid changes on foldable devices
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const newWidth = window.innerWidth;
        setWindowWidth(newWidth);
        // Ensure ads stay visible during resize
        setIsVisible(true);
      }, 300); // Debounce resize events
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Ensure visibility is maintained
  useEffect(() => {
    setIsVisible(true);
  }, [initialScreenType]);

  // Determine initial screen type and prevent switching on foldable devices
  useEffect(() => {
    if (initialScreenType === null) {
      // Use a more robust detection that waits for DOM to be ready
      const detectScreenType = () => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        
        // For foldable devices, be more conservative about desktop detection
        // Consider it desktop only if it's clearly a desktop-sized screen
        const isDesktop = width >= 1200 || (width >= 1024 && height >= 600);
        setInitialScreenType(isDesktop ? 'desktop' : 'mobile');
      };

      // Try immediately, then with a small delay to ensure DOM is ready
      detectScreenType();
      
      const timer = setTimeout(detectScreenType, 100);
      return () => clearTimeout(timer);
    }
  }, [initialScreenType]);

  // Load AdSense script and initialize ads
  useEffect(() => {
    const loadAdSense = async () => {
      if (typeof window === 'undefined') return;

      // Suppress tracking prevention console warnings for cleaner logs
      suppressTrackingWarnings();

      // Check for tracking prevention issues
      if (isTrackingPreventionEnabled()) {
        console.log('Tracking prevention detected - ads may not display properly');
      }

      // Check if AdSense script is already loaded
      if (window.adsbygoogle) {
        setAdsLoaded(true);
        return;
      }

      // Check if script is already in DOM
      if (document.querySelector('script[src*="adsbygoogle.js"]')) {
        setAdsLoaded(true);
        return;
      }

      // Test if AdSense script can load (detect ad blockers)
      try {
        const canLoadScript = await checkAdSenseScriptLoad();
        if (!canLoadScript) {
          console.log('Ad blocker detected - showing fallback content');
          setIsAdBlockerDetected(true);
          return;
        }
      } catch (error) {
        console.log('Ad blocker detection failed:', error);
        setIsAdBlockerDetected(true);
        return;
      }

      // Load AdSense script
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1421631996968165';
      script.crossOrigin = 'anonymous';
      
      script.onload = () => {
        setAdsLoaded(true);
        console.log('AdSense script loaded successfully');
        
        // Set a timeout to show fallback if ads don't appear after script loads
        setTimeout(() => {
          // Check if any ads are actually visible
          const adElements = document.querySelectorAll('.adsbygoogle');
          const visibleAds = Array.from(adElements).filter(ad => {
            const rect = ad.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
          });
          
          if (visibleAds.length === 0) {
            console.log('No visible ads detected - showing fallback content');
            setShowFallback(true);
          }
        }, 3000); // Wait 3 seconds for ads to load
      };

      script.onerror = () => {
        console.log('Failed to load AdSense script - ad blocker detected');
        setIsAdBlockerDetected(true);
      };

      document.head.appendChild(script);
    };

    loadAdSense();
  }, []);

  // Don't render ads if not loaded, user disabled them, or tracking prevention is blocking
  if (!shouldShowAds() || !isVisible) {
    return null;
  }

  // Use initial screen type to prevent switching between mobile/desktop on foldable devices
  // Fallback to mobile if screen type is not yet determined
  const shouldShowDesktop = initialScreenType === 'desktop';
  const shouldShowMobile = initialScreenType === 'mobile' || initialScreenType === null;

  // Debug logging for mobile ad issues
  useEffect(() => {
    console.log('AdManager Debug:', {
      initialScreenType,
      shouldShowDesktop,
      shouldShowMobile,
      windowWidth,
      isAdBlockerDetected,
      showFallback,
      adsLoaded
    });
  }, [initialScreenType, shouldShowDesktop, shouldShowMobile, windowWidth, isAdBlockerDetected, showFallback, adsLoaded]);

  // Show fallback content if ad blocker is detected or ads fail to load
  if (isAdBlockerDetected || showFallback) {
    // Use Firefox-specific fallback for Firefox browsers
    const isFirefox = isFirefoxETPEnabled();
    
    return (
      <div className={`ad-manager ${className}`}>
        {/* Desktop Fallback */}
        {shouldShowDesktop && (
          isFirefox ? (
            <FirefoxAdFallback 
              size="desktop"
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-40"
            />
          ) : (
            <FallbackAd 
              size="desktop"
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-40"
            />
          )
        )}

        {/* Mobile Fallback */}
        {shouldShowMobile && (
          isFirefox ? (
            <FirefoxAdFallback 
              size="mobile"
              className="fixed bottom-0 left-0 right-0 z-50"
            />
          ) : (
            <FallbackAd 
              size="mobile"
              className="fixed bottom-0 left-0 right-0 z-50"
            />
          )
        )}
      </div>
    );
  }

  return (
    <div className={`ad-manager ${className}`}>
      {/* Desktop Ad - Right Sidebar (Positioned relative to spinner container) */}
      {shouldShowDesktop && (
        <DesktopAd 
          className="absolute right-4 top-1/2 transform -translate-y-1/2 z-40"
          adSize="300x600"
        />
      )}

      {/* Mobile/Tablet Sticky Ad */}
      {shouldShowMobile && (
        <MobileStickyAd 
          adSize="320x50"
        />
      )}
    </div>
  );
};

export default AdManager;
